import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { useBasket } from "../components/BasketProvider";
import {
  Button,
  Row,
  Col,
  Card,
  Image,
  Typography,
  notification,
  Tabs,
  Rate,
  Input,
  List,
  Tag,
  Space,
  Divider,
  Spin,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  ArrowLeftOutlined,
  ShoppingCartOutlined,
  CheckOutlined,
  WhatsAppOutlined,
  InstagramOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  MailOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

function ProductDetails() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { dispatch } = useBasket();

  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [rating, setRating] = useState(4);
  const [reviews, setReviews] = useState([]);
  const [reviewText, setReviewText] = useState("");

  const VAT_RATE = 0.16; // 16% VAT
  const WHATSAPP_NUMBER = "+254729335354";
  const INSTAGRAM_PROFILE_URL = "https://www.instagram.com/dollar_furniture_ke/?hl=en";

  const openNotification = (message, description) => {
    notification.success({
      message,
      description,
      placement: "topRight",
    });
  };

  useEffect(() => {
    const fetchProductDetails = async () => {
      const docRef = doc(db, "products", productId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const productData = docSnap.data();
        setProduct(productData);
        setSelectedImage(productData.images?.[0]);
        setReviews(productData.reviews || []);
      } else {
        console.error("No such document!");
      }
    };

    fetchProductDetails();
  }, [productId]);

  const calculatePrices = () => {
    if (!product) return {};

    const basePrice = product.price;
    const discountAmount =
      product.discount > 0 ? (basePrice * product.discount) / 100 : 0;
    const priceAfterDiscount = basePrice - discountAmount;
    const vatAmount = priceAfterDiscount * VAT_RATE;
    const finalPrice = priceAfterDiscount; // VAT already included
    const originalPriceWithVAT = basePrice * (1 + VAT_RATE);

    return {
      basePrice,
      discountAmount,
      priceAfterDiscount,
      vatAmount,
      finalPrice,
      originalPriceWithVAT,
    };
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex - 1 + product.images.length) % product.images.length
    );
    setSelectedImage(
      product.images[currentImageIndex - 1] || product.images[product.images.length - 1]
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % product.images.length);
    setSelectedImage(product.images[currentImageIndex + 1] || product.images[0]);
  };

  const handleThumbnailClick = (index) => {
    setSelectedImage(product.images[index]);
    setCurrentImageIndex(index);
  };

  const handleQuantityChange = (type) => {
    setQuantity((prevQuantity) =>
      type === "increment" ? prevQuantity + 1 : Math.max(prevQuantity - 1, 1)
    );
  };

  const handleAddToBasket = () => {
    const prices = calculatePrices();

    dispatch({
      type: "ADD_TO_BASKET",
      payload: {
        id: productId,
        title: product.title,
        price: prices.finalPrice,
        originalPrice: prices.basePrice,
        vatAmount: prices.vatAmount,
        vatIncluded: true,
        image: product.images?.[0],
        quantity,
        category: product.category,
      },
    });
    openNotification("Product Added", `${product.title} has been added to your basket.`);
  };

  const handleBuyNow = () => {
    handleAddToBasket();
    navigate("/basket");
  };

  // ----- WhatsApp Order (includes link + product details) -----
  const handleWhatsAppOrder = () => {
    const prices = calculatePrices();
    const totalPrice = prices.finalPrice * quantity;
    const productLink = window.location.href;
    const message = `Hello! I'd like to order:\n\n*Product:* ${product.title}\n*Product ID:* ${product.productNo || productId}\n*Quantity:* ${quantity}\n*Price per item:* Ksh ${prices.finalPrice.toFixed(2)}\n*Total:* Ksh ${totalPrice.toFixed(2)}\n\n*Product Link:* ${productLink}\n\nPlease confirm my order.`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, "_blank");
  };

  // ----- Instagram Order (copy details + link, then open profile) -----
  const handleInstagramOrder = async () => {
    const prices = calculatePrices();
    const totalPrice = prices.finalPrice * quantity;
    const productLink = window.location.href;
    const details = `Product: ${product.title}\nProduct ID: ${product.productNo || productId}\nQuantity: ${quantity}\nTotal: Ksh ${totalPrice.toFixed(2)}\nLink: ${productLink}`;

    try {
      await navigator.clipboard.writeText(details);
      notification.success({
        message: "Product Details Copied!",
        description:
          "We've copied the product information. Now open Instagram, send this message to @dollar_furniture_ke to place your order.",
        placement: "topRight",
        duration: 5,
      });
      window.open(INSTAGRAM_PROFILE_URL, "_blank");
    } catch (err) {
      notification.error({
        message: "Copy Failed",
        description: "Please manually copy the product link from your browser.",
        placement: "topRight",
      });
      window.open(INSTAGRAM_PROFILE_URL, "_blank");
    }
  };

  // ----- Share on Instagram (just copy link + short message, open Instagram) -----
  const shareOnInstagram = async () => {
    const productLink = window.location.href;
    const shareText = `Check out this product: ${product.title}\n${productLink}`;
    try {
      await navigator.clipboard.writeText(shareText);
      notification.success({
        message: "Link Copied!",
        description: "Product link copied. Open Instagram and paste it anywhere (DM, story, or post).",
        placement: "topRight",
      });
      window.open(INSTAGRAM_PROFILE_URL, "_blank");
    } catch (err) {
      notification.error({
        message: "Copy Failed",
        description: "Please manually copy the link from your browser.",
        placement: "topRight",
      });
    }
  };

  // ----- Share on WhatsApp (social share, not order) -----
  const shareOnWhatsApp = () => {
    const productLink = window.location.href;
    const text = `Check out this product: ${product.title}\n${productLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  // ----- Share via Email -----
  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Check out ${product.title} from Dollar Furniture`);
    const body = encodeURIComponent(
      `I found this product: ${product.title}\n\nSee more: ${window.location.href}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // ----- Copy product link -----
  const copyProductLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notification.success({
        message: "Link Copied!",
        description: "Product link copied to clipboard. Share it anywhere!",
        placement: "topRight",
      });
    } catch (err) {
      notification.error({
        message: "Copy Failed",
        description: "Unable to copy link. Please try manually.",
        placement: "topRight",
      });
    }
  };

  const handleAddReview = async () => {
    if (!reviewText.trim()) {
      notification.warning({
        message: "Empty Review",
        description: "Please write a review before submitting.",
      });
      return;
    }

    const newReview = { rating, text: reviewText, date: new Date().toISOString() };
    try {
      const docRef = doc(db, "products", productId);
      await updateDoc(docRef, {
        reviews: arrayUnion(newReview),
      });
      setReviews((prev) => [...prev, newReview]);
      setReviewText("");
      setRating(4);
      notification.success({
        message: "Review Added",
        description: "Thank you for your feedback!",
      });
    } catch (error) {
      console.error("Error adding review:", error);
      notification.error({
        message: "Error",
        description: "Failed to add your review. Please try again later.",
      });
    }
  };

  if (!product) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" tip="Loading product details..." />
      </div>
    );
  }

  const prices = calculatePrices();
  const totalPrice = prices.finalPrice * quantity;

  return (
    <div
      style={{
        padding: "20px",
        maxWidth: "1400px",
        margin: "0 auto",
        background: "#f8f9fa",
      }}
    >
      {/* Back Button */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/product")}
            style={{ color: "#1890ff", fontSize: "16px", marginBottom: "20px" }}
          >
            Back to Products
          </Button>
        </Col>
      </Row>

      <Row gutter={[32, 32]}>
        {/* Image Gallery */}
        <Col xs={24} md={10}>
          <Card
            bordered={false}
            style={{
              borderRadius: "15px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
            bodyStyle={{ padding: "20px" }}
          >
            <div style={{ position: "relative" }}>
              <Image
                src={selectedImage}
                alt={product.title}
                style={{
                  width: "100%",
                  height: "400px",
                  objectFit: "contain",
                  borderRadius: "10px",
                  marginBottom: "20px",
                }}
                preview={false}
              />
              {product.images && product.images.length > 1 && (
                <>
                  <Button
                    onClick={handlePrevImage}
                    icon={<LeftOutlined />}
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 2,
                    }}
                  />
                  <Button
                    onClick={handleNextImage}
                    icon={<RightOutlined />}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 2,
                    }}
                  />
                </>
              )}
            </div>

            {product.images && product.images.length > 1 && (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  overflowX: "auto",
                  padding: "10px 0",
                }}
              >
                {product.images.map((image, index) => (
                  <Image
                    key={index}
                    src={image}
                    alt={`Thumbnail ${index}`}
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      cursor: "pointer",
                      border:
                        selectedImage === image ? "3px solid #1890ff" : "1px solid #d9d9d9",
                      flexShrink: 0,
                    }}
                    onClick={() => handleThumbnailClick(index)}
                    preview={false}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* Shopping Options Card - WhatsApp & Instagram Order */}
          <Card
            title="Shop Via"
            bordered={false}
            style={{
              marginTop: "20px",
              borderRadius: "15px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Button
                type="primary"
                icon={<WhatsAppOutlined />}
                block
                size="large"
                style={{
                  background: "#25D366",
                  borderColor: "#25D366",
                  height: "50px",
                  fontSize: "16px",
                }}
                onClick={handleWhatsAppOrder}
              >
                Order on WhatsApp
              </Button>

              <Button
                type="primary"
                icon={<InstagramOutlined />}
                block
                size="large"
                style={{
                  background:
                    "linear-gradient(45deg, #405DE6, #5851DB, #833AB4, #C13584, #E1306C, #FD1D1D)",
                  border: "none",
                  height: "50px",
                  fontSize: "16px",
                }}
                onClick={handleInstagramOrder}
              >
                Order on Instagram
              </Button>

              <div style={{ textAlign: "center", padding: "10px" }}>
                <Text type="secondary">Or shop directly on our platform</Text>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Product Details */}
        <Col xs={24} md={14}>
          <Card
            bordered={false}
            style={{
              borderRadius: "15px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              marginBottom: "20px",
            }}
          >
            <Title level={2} style={{ marginBottom: "10px" }}>
              {product.title}
            </Title>

            <div style={{ marginBottom: "20px" }}>
              <Rate
                disabled
                defaultValue={product.averageRating || 4}
                style={{ fontSize: "18px" }}
              />
              <Text type="secondary" style={{ marginLeft: "10px" }}>
                ({reviews.length} reviews)
              </Text>
            </div>

            {/* Price Display */}
            <div
              style={{
                background: "#f8f9fa",
                padding: "20px",
                borderRadius: "10px",
                marginBottom: "20px",
              }}
            >
              <Title level={3} style={{ marginBottom: "20px" }}>
                Price Details
              </Title>

              <Space direction="vertical" style={{ width: "100%" }} size="small">
                {product.discount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text>Original Price:</Text>
                    <Text delete style={{ color: "#ff4d4f" }}>
                      Ksh {prices.originalPriceWithVAT.toFixed(2)}
                    </Text>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text strong>Our Price:</Text>
                  <Title level={3} style={{ margin: 0, color: "#52c41a" }}>
                    Ksh {prices.finalPrice.toFixed(2)}
                  </Title>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">
                    <InfoCircleOutlined style={{ marginRight: "5px" }} />
                    Includes 16% VAT: Ksh {prices.vatAmount.toFixed(2)}
                  </Text>
                  <Tag color="green" style={{ fontSize: "12px" }}>
                    VAT Included
                  </Tag>
                </div>

                {product.discount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text>You Save:</Text>
                    <Text strong style={{ color: "#52c41a" }}>
                      Ksh {(prices.discountAmount + prices.discountAmount * VAT_RATE).toFixed(2)}
                    </Text>
                  </div>
                )}
              </Space>
            </div>

            {/* Product Info */}
            <div style={{ marginBottom: "20px" }}>
              <Title level={4}>Product Information</Title>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text strong>Category:</Text>
                  <br />
                  <Tag color="blue">{product.category}</Tag>
                </Col>
                <Col span={12}>
                  <Text strong>Product Number:</Text>
                  <br />
                  <Text code>{product.productNo || productId}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Available Quantity:</Text>
                  <br />
                  <Text>{product.quantity} units</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Size:</Text>
                  <br />
                  <Text>{product.size || "Standard"}</Text>
                </Col>
                {product.discount > 0 && (
                  <Col span={24}>
                    <Tag color="red" style={{ fontSize: "16px", padding: "5px 15px" }}>
                      🎉 {product.discount}% OFF
                    </Tag>
                  </Col>
                )}
              </Row>
            </div>

            <Divider />

            {/* Quantity & Actions */}
            <div style={{ marginBottom: "30px" }}>
              <Title level={4} style={{ marginBottom: "15px" }}>
                Quantity
              </Title>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  marginBottom: "30px",
                }}
              >
                <Button.Group>
                  <Button
                    onClick={() => handleQuantityChange("decrement")}
                    size="large"
                    style={{ width: "50px", fontSize: "18px" }}
                  >
                    -
                  </Button>
                  <Input
                    value={quantity}
                    size="large"
                    style={{
                      width: "80px",
                      textAlign: "center",
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                    readOnly
                  />
                  <Button
                    onClick={() => handleQuantityChange("increment")}
                    size="large"
                    style={{ width: "50px", fontSize: "18px" }}
                  >
                    +
                  </Button>
                </Button.Group>

                <Text strong style={{ fontSize: "16px" }}>
                  Total: Ksh {totalPrice.toFixed(2)}
                </Text>
              </div>

              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Button
                  type="primary"
                  icon={<ShoppingCartOutlined />}
                  size="large"
                  block
                  style={{
                    height: "50px",
                    fontSize: "16px",
                    background: "linear-gradient(45deg, #1890ff, #0050b3)",
                    border: "none",
                  }}
                  onClick={handleAddToBasket}
                >
                  Add to Basket
                </Button>

                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  size="large"
                  block
                  style={{
                    height: "50px",
                    fontSize: "16px",
                    background: "linear-gradient(45deg, #52c41a, #389e0d)",
                    border: "none",
                  }}
                  onClick={handleBuyNow}
                >
                  Buy Now
                </Button>
              </Space>
            </div>

            {/* Share Product Card - Only WhatsApp, Instagram, Email, Copy Link */}
            <Card
              title={
                <span>
                  <ShareAltOutlined style={{ marginRight: 8 }} />
                  Share this Product
                </span>
              }
              bordered
              style={{ marginTop: "20px" }}
            >
              <Space wrap size="middle" style={{ justifyContent: "center", width: "100%" }}>
                <Button
                  icon={<WhatsAppOutlined />}
                  onClick={shareOnWhatsApp}
                  style={{ backgroundColor: "#25D366", color: "white", border: "none" }}
                >
                  WhatsApp
                </Button>
                <Button
                  icon={<InstagramOutlined />}
                  onClick={shareOnInstagram}
                  style={{
                    background: "linear-gradient(45deg, #405DE6, #5851DB, #833AB4, #C13584, #E1306C, #FD1D1D)",
                    color: "white",
                    border: "none",
                  }}
                >
                  Instagram
                </Button>
                <Button
                  icon={<MailOutlined />}
                  onClick={shareViaEmail}
                  style={{ backgroundColor: "#EA4335", color: "white", border: "none" }}
                >
                  Email
                </Button>
                <Button
                  icon={<LinkOutlined />}
                  onClick={copyProductLink}
                  style={{ backgroundColor: "#6c757d", color: "white", border: "none" }}
                >
                  Copy Link
                </Button>
              </Space>
            </Card>

            {/* Contact Info */}
            <Card title="Need Help?" bordered style={{ marginTop: "20px" }}>
              <Space direction="vertical">
                <div>
                  <WhatsAppOutlined style={{ color: "#25D366", fontSize: "20px" }} />
                  <Text strong style={{ marginLeft: "10px" }}>
                    WhatsApp:
                  </Text>
                  <Text style={{ marginLeft: "10px" }}>{WHATSAPP_NUMBER}</Text>
                </div>
                <div>
                  <InstagramOutlined
                    style={{
                      fontSize: "20px",
                      background: "linear-gradient(45deg, #405DE6, #C13584)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  />
                  <Text strong style={{ marginLeft: "10px" }}>
                    Instagram:
                  </Text>
                  <a
                    href={INSTAGRAM_PROFILE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: "10px" }}
                  >
                    @dollar_furniture_ke
                  </a>
                </div>
              </Space>
            </Card>
          </Card>
        </Col>
      </Row>

      {/* Tabs Section */}
      <Row gutter={[16, 16]} style={{ marginTop: "30px" }}>
        <Col span={24}>
          <Card
            bordered={false}
            style={{
              borderRadius: "15px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Tabs defaultActiveKey="1" type="card">
              <TabPane tab="Description" key="1">
                <Paragraph style={{ fontSize: "16px", lineHeight: "1.8" }}>
                  {product.description}
                </Paragraph>
              </TabPane>

              <TabPane tab={`Reviews (${reviews.length})`} key="2">
                <div style={{ marginBottom: "20px" }}>
                  <Rate value={rating} onChange={setRating} style={{ fontSize: "20px" }} />
                  <TextArea
                    rows={4}
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Share your experience with this product..."
                    style={{ marginTop: "10px" }}
                    maxLength={500}
                  />
                  <Button
                    type="primary"
                    onClick={handleAddReview}
                    style={{ marginTop: "10px" }}
                    icon={<MessageOutlined />}
                  >
                    Submit Review
                  </Button>
                </div>

                <List
                  dataSource={reviews}
                  renderItem={(item, index) => (
                    <List.Item key={index}>
                      <List.Item.Meta
                        avatar={
                          <Rate
                            disabled
                            value={item.rating}
                            style={{ fontSize: "14px" }}
                          />
                        }
                        title={<Text>{item.text}</Text>}
                        description={
                          <Text type="secondary">
                            {new Date(item.date).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              </TabPane>

              <TabPane tab="FAQs" key="3">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Card size="small" title="Delivery Information" style={{ marginBottom: "10px" }}>
                    <Text>We deliver nationwide. Delivery time varies by location.</Text>
                  </Card>
                  <Card size="small" title="Payment Methods" style={{ marginBottom: "10px" }}>
                    <Text>We accept M-Pesa, credit cards, and bank transfers.</Text>
                  </Card>
                  <Card size="small" title="Return Policy">
                    <Text>30-day return policy for unused items in original packaging.</Text>
                  </Card>
                </Space>
              </TabPane>

              <TabPane tab="Shipping & Returns" key="4">
                <Image
                  src="https://firebasestorage.googleapis.com/v0/b/nairobi-d8ab0.appspot.com/o/dollar%20254%2FWhatsApp%20Image%202024-12-18%20at%2011.25.45%20AM.jpeg?alt=media&token=bc010298-32a2-42b1-b5db-4c4a00feed25"
                  alt="Delivery Information"
                  style={{
                    width: "100%",
                    borderRadius: "10px",
                    marginBottom: "20px",
                  }}
                />
                <Image
                  src="https://firebasestorage.googleapis.com/v0/b/nairobi-d8ab0.appspot.com/o/dollar%20254%2FWhatsApp%20Image%202024-12-18%20at%2011.42.29%20AM%20(1).jpeg?alt=media&token=2899b944-1951-4a31-81f3-cbc57a36f810"
                  alt="Safe Checkout"
                  style={{
                    width: "100%",
                    borderRadius: "10px",
                  }}
                />
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default ProductDetails;